import React, { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Camera, RefreshCw, Loader2, ChevronLeft, ChevronRight, ExternalLink, Instagram } from "lucide-react";
import { toast } from "sonner";
import { exportStory } from "@/lib/storyExport";

const FALLBACK_BANNER = "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?auto=format&fit=crop&w=1600&q=80";

export default function AiGalleryPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroIdx, setHeroIdx] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events?limit=200");
      const withGallery = (Array.isArray(data) ? data : []).filter(
        (e) => e.ai_photo_gallery_url || e.external_gallery_url || e.ai_gallery_qr_path
      );
      withGallery.sort((a, b) => new Date(b.date) - new Date(a.date));
      setEvents(withGallery);
      setHeroIdx(0);
    } catch (err) {
      toast.error("Failed to load AI Gallery");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const hero = events.slice(0, 5);

  return (
    <Layout>
      {/* Iter 47 — THEMED BLUE STRIP (magazine signature) */}
      <section className="themed-blue sticker-wall py-8 overflow-hidden" data-testid="aip-themed-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/85">● AI Camera · 99% face match</div>
            <h1 className="font-fancy uppercase text-white leading-[0.9] mt-1" style={{ fontSize: "clamp(32px, 5vw, 56px)", textShadow: "3px 3px 0 rgba(0,0,0,0.3)" }}>
              Your night. <span className="text-striped">Delivered.</span>
            </h1>
          </div>
          <a href="https://ai.eventom.online" target="_blank" rel="noreferrer" className="btn-pill-white !text-[11px]">Access AI Gallery →</a>
        </div>
      </section>
      <section className="relative overflow-hidden border-b border-line" style={{ background: "radial-gradient(ellipse at top, rgba(0,212,255,0.28), transparent 60%), radial-gradient(ellipse at bottom, rgba(255,77,109,0.25), transparent 60%), #0A0A0A" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
            <div>
              <div className="chip bg-skyx/20 text-skyx mb-2">Powered by Eventom AI</div>
              <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.9]">Your event, <span className="grad-text-hot">in every frame.</span></h1>
              <p className="mt-2 text-text/85 max-w-2xl">Selfie inside the gallery → we surface every photo of you from that event, in seconds.</p>
            </div>
            <button onClick={load} disabled={loading} className="btn-lime !py-2 !px-4 !text-xs" data-testid="ai-gallery-refresh">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
            </button>
          </div>

          {hero.length > 0 && (
            <div className="relative rounded-3xl overflow-hidden card-lift" data-testid="ai-gallery-hero" style={{ minHeight: 280 }}>
              <img src={hero[heroIdx].banner || FALLBACK_BANNER} alt={hero[heroIdx].title} className="w-full aspect-[16/8] object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, rgba(10,10,10,0.9))" }} />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <div className="chip bg-skyx/30 text-skyx mb-2">Featured Gallery</div>
                  <div className="font-display uppercase text-3xl md:text-4xl leading-tight text-white">{hero[heroIdx].title}</div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-lime mt-1">{hero[heroIdx].date?.slice(0, 10)} · {hero[heroIdx].city || hero[heroIdx].location}</div>
                </div>
                <a href={hero[heroIdx].ai_photo_gallery_url || hero[heroIdx].external_gallery_url || "https://ai.eventom.online"} target="_blank" rel="noreferrer"
                  data-testid="ai-gallery-hero-cta" className="btn-primary" style={{ background: "linear-gradient(135deg, #00D4FF, #7B2FFF)" }}>
                  <Camera className="w-4 h-4" /> Open Gallery <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
              {hero.length > 1 && (
                <>
                  <button onClick={() => setHeroIdx((i) => (i - 1 + hero.length) % hero.length)} data-testid="ai-hero-prev" className="absolute left-3 top-1/2 -translate-y-1/2 icon-circle bg-black/60 backdrop-blur"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={() => setHeroIdx((i) => (i + 1) % hero.length)} data-testid="ai-hero-next" className="absolute right-3 top-1/2 -translate-y-1/2 icon-circle bg-black/60 backdrop-blur"><ChevronRight className="w-4 h-4" /></button>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="chip bg-lime/10 text-lime mb-3">All Gallery Events</div>
        <h2 className="font-display uppercase text-3xl md:text-4xl mb-6">Browse every event.</h2>
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="glass rounded-3xl aspect-[4/3] animate-pulse" />)}</div>
        ) : events.length === 0 ? (
          <div className="glass rounded-3xl p-8 text-center font-mono text-sm text-muted">No gallery-enabled events yet. Organisers can attach a gallery link from the event editor.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="ai-gallery-grid">
            {events.map((e) => (
              <div key={e.id} className="relative">
                <a href={e.ai_photo_gallery_url || e.external_gallery_url || "https://ai.eventom.online"} target="_blank" rel="noreferrer"
                  data-testid={`ai-gallery-card-${e.id}`} className="glass !rounded-2xl overflow-hidden card-lift block">
                  <div className="aspect-square overflow-hidden">
                    <img src={e.banner || FALLBACK_BANNER} alt={e.title} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <div className="font-heading font-bold text-xs leading-tight line-clamp-1">{e.title}</div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-muted mt-0.5 truncate">{e.date?.slice(0, 10)} · {e.city || e.location}</div>
                    <div className="mt-1.5 inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-widest text-lime">
                      Gallery <ExternalLink className="w-2.5 h-2.5" />
                    </div>
                  </div>
                </a>
                <button onClick={() => exportStory({ imageUrl: e.banner || FALLBACK_BANNER, title: e.title, city: e.city || e.location, date: e.date?.slice(0, 10) })}
                  data-testid={`story-export-${e.id}`}
                  className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-lime text-ink font-mono text-[9px] font-bold uppercase tracking-widest shadow-glow-lime hover:-translate-y-0.5 active:scale-95 transition-all">
                  <Instagram className="w-3 h-3" /> Story
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
